// @ts-nocheck
import siteMessage from "@insite/client-framework/SiteMessage";
import { StepForm, StepFormStep } from "@store/Framework/Store/Bunzl/Pages/StepForm/StepFormReducer";
import { TermsSignatureData } from "@store/Library/Widgets/CreditForm/CreditFormForm";
import { TradeReference } from "@store/Library/Widgets/CreditForm/useCreditFormTradeReferences";

export interface CreditFormData {
    company: CreditFormCompany;
    type: "credit" | "term";
    tradeReferences: TradeReferenceData[];
    accountsPayable: AccountsPayable;
    billTo: CreditFormShippingAddress;
    shipTos: CreditFormShippingAddress[];
    principal: CreditFormPrincipal;
    taxInformation: CreditFormTaxInformation;
    submitter: TermsSignatureData;
    banking: CreditFormBankingInformation;
    website: string;
}

interface CreditFormCompany {
    companyName: string;
    businessType: string;
    emailForInvoices: string;
    websiteAddress: string;
    previousBusinessNames: string;
    yearsInOperation: string;
}

interface CreditFormBankingInformation {
    name: string;
    address: string;
    city: string;
    province: string;
    postalCode: string;
    phoneNumber: string;
    accountName: string;
    accountNumber: string;
    contactName: string;
}

interface TradeReferenceData {
    name: string;
    address: string;
    city: string;
    province: string;
    postalCode: string;
    phoneNumber: string;
    faxNumber: string;
    email: string;
    contactName: string;
}

interface AccountsPayable {
    contactName: string;
    phoneNumber: string;
    email: string;
}

interface CreditFormShipping {
    billingAddress: CreditFormShippingAddress;
    shippingAddress: CreditFormShippingAddress[];
}

interface CreditFormShippingAddress {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    phoneNumber: string;
    faxNumber: string;
}
interface CreditFormPrincipal {
    title: string;
    name: string;
    address: string;
    phoneNumber: string;
}

interface CreditFormTaxInformation {
    pstExempt?: string;
    pstExemptionNumber?: string;
    gstExempt?: string;
    gstExemptionNumber?: string;
}

const getCompany = (step: StepFormStep | undefined): CreditFormCompany => {
    return {
        companyName: step?.inputs?.companyName?.value ?? "",
        businessType: step?.inputs?.businessType?.value ?? "",
        emailForInvoices: step?.inputs?.emailForInvoices?.value ?? "",
        websiteAddress: step?.inputs?.websiteAddress?.value ?? "",
        previousBusinessNames: step?.inputs?.previousBusinessNames?.value ?? "",
        yearsInOperation: step?.inputs?.yearsInOperation?.value ?? "",
    };
};

const getTradeReferences = (tradeReferenceStep: TradeReference[] | undefined): TradeReferenceData[] => {
    return (
        tradeReferenceStep?.map(tradeReference => {
            return {
                name: tradeReference.referenceName?.value,
                address: tradeReference.referenceAddress?.value,
                city: tradeReference.referenceCity?.value,
                province: tradeReference.referenceProvince?.value,
                postalCode: tradeReference.referencePostalCode?.value,
                phoneNumber: tradeReference.referencePhoneNumber?.value,
                faxNumber: tradeReference.referenceFaxNumber?.value,
                email: tradeReference.referenceEmail?.value,
                contactName: tradeReference.referenceContactName?.value,
            };
        }) ?? []
    );
};

const getAccountsPayable = (step: StepFormStep | undefined): AccountsPayable => {
    return {
        contactName: step?.inputs?.accountContactName?.value ?? "",
        phoneNumber: step?.inputs?.accountPhoneNumber?.value ?? "",
        email: step?.inputs?.accountEmail?.value ?? "",
    };
};
const getBankingInformation = (step: StepFormStep | undefined): CreditFormBankingInformation => {
    return {
        name: step?.inputs?.bankName?.value ?? "",
        address: step?.inputs?.bankAddress?.value ?? "",
        city: step?.inputs?.bankCity?.value ?? "",
        province: step?.inputs?.province?.value ?? "",
        postalCode: step?.inputs?.postalCode?.value ?? "",
        phoneNumber: step?.inputs?.phoneNumber?.value ?? "",
        accountName: step?.inputs?.bankAccountName?.value ?? "",
        accountNumber: step?.inputs?.bankAccountNumber?.value ?? "",
        contactName: step?.inputs?.bankContactName?.value ?? "",
    };
};
const getType = (step: StepFormStep | undefined): "credit" | "term" => {
    return step?.inputs?.accountType?.value === siteMessage("CreditForm_TermAccount") ? "term" : "credit";
};

const getShipping = (step: StepFormStep | undefined): CreditFormShipping => {
    const billingAddress = {
        address: step?.inputs?.billingCompanyAddress?.value ?? "",
        city: step?.inputs?.billingCity?.value ?? "",
        province: step?.inputs?.billingProvince?.value ?? "",
        postalCode: step?.inputs?.billingPostalCode?.value ?? "",
        phoneNumber: step?.inputs?.billingPhoneNumber?.value ?? "",
        faxNumber: step?.inputs?.billingFaxNumber?.value ?? "",
    };
    const shippingAddress = step?.inputs?.shippingCity
        ? [
              {
                  address: step?.inputs?.shippingCompanyAddress?.value ?? "",
                  city: step?.inputs?.shippingCity?.value ?? "",
                  province: step?.inputs?.shippingProvince?.value ?? "",
                  postalCode: step?.inputs?.shippingPostalCode?.value ?? "",
                  phoneNumber: step?.inputs?.shippingPhoneNumber?.value ?? "",
                  faxNumber: step?.inputs?.shippingFaxNumber?.value ?? "", // this doesn't exist on form
              },
          ]
        : [billingAddress];
    return {
        billingAddress,
        shippingAddress,
    };
};

const getPrincipal = (step: StepFormStep | undefined): CreditFormPrincipal => {
    return {
        title: step?.inputs?.principalTitle?.value ?? "",
        name: step?.inputs?.principalName?.value ?? "",
        address: step?.inputs?.principalAddress?.value ?? "",
        phoneNumber: step?.inputs?.principalPhoneNumber?.value ?? "",
    };
};

const getTaxInformation = (step: StepFormStep | undefined): CreditFormTaxInformation => {
    return {
        pstExempt: step?.inputs?.PSTExempt?.value ?? "",
        pstExemptionNumber: step?.inputs?.PstExemptionNumber?.value ?? "",
        gstExempt: step?.inputs?.GSTExempt?.value ?? "",
        gstExemptionNumber: step?.inputs?.GstExemptionNumber?.value ?? "",
    };
};

export default (stepForm: StepForm, signatureData: TermsSignatureData, website: string): CreditFormData => {
    const basicDetailsStep = stepForm.steps.find(step => step.label === "Basic Details");
    const principalAndTaxStep = stepForm.steps.find(step => step.label === "Principal and Tax Information");
    const tradeReferenceStep =
        (stepForm.steps.find(step => step.label === "Trade References")?.tradeReferences as TradeReference[]) ??
        ([] as TradeReference[]);
    const bankingInformationStep = stepForm.steps.find(step => step.label === "Banking Information");

    const tradeReferences = getTradeReferences(tradeReferenceStep);
    const company = getCompany(basicDetailsStep);
    const accountsPayable = getAccountsPayable(basicDetailsStep);
    const type = getType(basicDetailsStep);
    const shipping = getShipping(basicDetailsStep);
    const principal = getPrincipal(principalAndTaxStep);
    const taxInformation = getTaxInformation(principalAndTaxStep);
    const banking = getBankingInformation(bankingInformationStep);
    return {
        company,
        type,
        banking,
        tradeReferences,
        accountsPayable,
        billTo: shipping.billingAddress,
        shipTos: shipping.shippingAddress,
        principal,
        taxInformation,
        submitter: signatureData,
        website: window.location.hostname,
    };
};
